import * as React from 'react';

import type { AttachmentPhase } from '../hooks/useAttachmentFile';
import styles from './DocumentLoading.module.css';

export type DocumentLoadingPhase = AttachmentPhase | 'notes';

export interface DocumentLoadingProps {
    phase: DocumentLoadingPhase;
    percent: number | null;
    loaded: number;
    total: number | null;
    error?: string | null;
    onRetry?: () => void;
    onDownload?: () => void;
}

function formatBytes(bytes: number): string {
    if (bytes <= 0) return '0 Б';

    const units = ['Б', 'КБ', 'МБ', 'ГБ'];
    const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / 1024 ** exponent;
    const formatted = exponent === 0 ? `${value}` : value.toFixed(1);

    return `${formatted} ${units[exponent]}`;
}

export const DocumentLoading: React.FC<DocumentLoadingProps> = ({ phase, percent, loaded, total, error, onRetry, onDownload }) => {
    if (phase === 'error') {
        return (
            <div className={styles.wrap}>
                <div className={styles.error}>
                    <i className={`pi pi-exclamation-triangle ${styles.errorIcon}`} />
                    <span className={styles.errorText}>{error ?? 'Не удалось скачать файл'}</span>
                    <div className={styles.errorActions}>
                        {onRetry ? (
                            <button type="button" className={styles.retryButton} onClick={onRetry}>
                                Повторить
                            </button>
                        ) : null}
                        {onDownload ? (
                            <button type="button" className={styles.downloadButton} onClick={onDownload}>
                                Скачать документ
                            </button>
                        ) : null}
                    </div>
                </div>
            </div>
        );
    }

    if (phase === 'downloading') {
        const bytesLabel = total ? `${formatBytes(loaded)} из ${formatBytes(total)}` : formatBytes(loaded);

        return (
            <div className={styles.wrap}>
                <div className={styles.label}>Загрузка документа…</div>
                <div className={styles.track}>
                    <div
                        className={`${styles.fill} ${percent === null ? styles.fillIndeterminate : ''}`}
                        style={percent === null ? undefined : { width: `${percent}%` }}
                    />
                </div>
                <div className={styles.bytes}>{bytesLabel}</div>
            </div>
        );
    }

    if (phase === 'notes') {
        return (
            <div className={styles.wrap}>
                <div className={styles.spinner} />
                <div className={styles.label}>Загрузка замечаний…</div>
            </div>
        );
    }

    return (
        <div className={styles.wrap}>
            <div className={styles.spinner} />
        </div>
    );
};
